import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { UseQueryConfig } from '@/api/types';
import {
  OpoContactOrderByOption,
  OpoContactTypeFilter,
  OpoOrganization,
  OpoPrimaryContactFilter,
  PaginatedOpoContactListQueryParams,
  PaginatedOpoOrganizationListQueryParams,
} from '@/api/apis/opo';
import { queryClient } from '@/queries';
import { opoQueryKeys, opoContactQueryKeys } from '@/queries/opo/opo.keys';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '@/queries/utils';

const allOrganizationPaginationParams: PaginatedOpoOrganizationListQueryParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

export const opoQueries = {
  all: () =>
    queryOptions({
      queryKey: opoQueryKeys.getList(allOrganizationPaginationParams),
      queryFn: () => api.opo.getOrganizationList(allOrganizationPaginationParams),
    }),
  list: (pagination: PaginatedOpoOrganizationListQueryParams) =>
    queryOptions({
      queryKey: opoQueryKeys.getList(pagination),
      queryFn: () => api.opo.getOrganizationList(pagination),
      placeholderData: keepPreviousData,
    }),
  infiniteList: (searchPagination: PaginatedOpoOrganizationListQueryParams) =>
    infiniteQueryOptions({
      queryKey: opoQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) => api.opo.getOrganizationList({ ...searchPagination, pageIndex }),
      placeholderData: keepPreviousData,
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
  item: (organizationId?: string | null) =>
    queryOptions({
      queryKey: opoQueryKeys.getItem(organizationId),
      queryFn: organizationId ? () => api.opo.getOrganizationById(organizationId) : skipToken,
    }),
};

export const opoContactQueries = {
  list: (pagination: PaginatedOpoContactListQueryParams) =>
    queryOptions({
      queryKey: opoContactQueryKeys.getList(pagination),
      queryFn: () => api.opo.getContactList(pagination),
      placeholderData: keepPreviousData,
    }),
  infiniteList: (searchPagination: PaginatedOpoContactListQueryParams) =>
    infiniteQueryOptions({
      queryKey: opoContactQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) => api.opo.getContactList({ ...searchPagination, pageIndex }),
      placeholderData: keepPreviousData,
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
  item: (contactId?: string | null) =>
    queryOptions({
      queryKey: opoContactQueryKeys.getItem(contactId),
      queryFn: contactId ? () => api.opo.getContactById(contactId) : skipToken,
    }),
};

const useOpoListQuery = (pagination: PaginatedOpoOrganizationListQueryParams, options?: UseQueryConfig) => {
  return useQuery({
    ...opoQueries.list(pagination),
    ...options,
  });
};

const useInfiniteOpoListQuery = (filters: PaginatedOpoOrganizationListQueryParams) => {
  return useInfiniteQuery(opoQueries.infiniteList({ itemsPerPage: 10, pageIndex: 0, ...filters }));
};

const useOpoByIdQuery = (id?: string | null) => {
  return useQuery({
    ...opoQueries.item(id),
    staleTime: 60 * 1000,
    initialData: () =>
      queryClient.getQueryData(opoQueries.all().queryKey)?.items.find((c: OpoOrganization) => c.id === id),
    initialDataUpdatedAt: () => queryClient.getQueryState(opoQueries.all().queryKey)?.dataUpdatedAt,
  });
};

const useOpoContactListQuery = (pagination: PaginatedOpoContactListQueryParams, options?: UseQueryConfig) => {
  return useQuery({
    ...opoContactQueries.list(pagination),
    ...options,
  });
};

const useInfiniteOpoContactListQuery = (filters: PaginatedOpoContactListQueryParams) => {
  return useInfiniteQuery(opoContactQueries.infiniteList({ itemsPerPage: 10, pageIndex: 0, ...filters }));
};

export const getOpoOrganizationsByIds = async (organizationIds: string[]) => {
  const uniqueIds = Array.from(new Set(organizationIds));

  if (uniqueIds.length) {
    return api.opo.getOrganizationList({
      organizationIds: uniqueIds,
      pageIndex: 0,
      itemsPerPage: uniqueIds.length,
    });
  }

  return null;
};

export const invalidateOpoOrganizationByIdQuery = async (organizationId?: string | null) => {
  if (!organizationId) return;

  await queryClient.invalidateQueries({
    queryKey: opoQueries.item(organizationId).queryKey,
  });
};

export const useOpoContactsByOrgQuery = (organizationId: string | undefined, options?: UseQueryConfig) =>
  useQuery({
    ...opoContactQueries.list({
      organizationIds: organizationId ? [organizationId] : [],
      pageIndex: 0,
      itemsPerPage: 100,
      primaryFilter: OpoPrimaryContactFilter.All,
      contactType: OpoContactTypeFilter.All,
      orderBy: OpoContactOrderByOption.CreatedAt,
      asc: false,
    }),
    ...options,
  });

export {
  useOpoListQuery,
  useOpoByIdQuery,
  useInfiniteOpoListQuery,
  useOpoContactListQuery,
  useInfiniteOpoContactListQuery,
};
